import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ProductRecommendation } from 'utils/eva';

import type { Message } from '../model/chat.types';
import { useChatMessageState } from './useChatMessageState';
import { useChatScrollAnchor } from './useChatScrollAnchor';

type UseChatMessagesContainerArgs = {
  messages: Message[];
  isThinking: boolean;
  isStreaming: boolean;
  activeAgentMessageId: string | null;
  activeStreamEventSeq: number;
  recommendations: ProductRecommendation | null;
  errorMessage: string | null;
  onSendMessage?: (message: string) => Promise<boolean>;
  onTypingComplete?: (isComplete: boolean) => void;
};

type MessageAnimationState = {
  shouldAnimate: boolean;
  shouldFadeOutAvatar: boolean;
  onTypingComplete?: () => void;
};

export function useChatMessagesContainer({
  messages,
  isThinking,
  isStreaming,
  activeAgentMessageId,
  activeStreamEventSeq,
  recommendations,
  errorMessage,
  onSendMessage,
  onTypingComplete,
}: UseChatMessagesContainerArgs) {
  const {
    lastAgentMessageIndex,
    showLogoOnRecommendation,
    showLogoOnError,
    showLogoOnLastAgentMessage,
    isAwaitingAgentResponse,
    scrollDependencies,
  } = useChatMessageState({
    messages,
    isThinking,
    isStreaming,
    recommendations,
    errorMessage,
    activeStreamEventSeq,
    activeAgentMessageId,
  });

  // Business rule: Messages restored from localStorage should not animate.
  const initialMessageIdsRef = useRef<Set<string> | null>(null);
  if (initialMessageIdsRef.current === null) {
    initialMessageIdsRef.current = new Set(messages.map((m) => m.id));
  }

  // Business rule: Track last agent message that completed typing to gate recommendations.
  const [typingCompleteMessageId, setTypingCompleteMessageId] = useState<
    string | null
  >(null);

  // Business rule: Use last agent message for scroll anchoring after responses.
  const lastAgentMessageId = useMemo(() => {
    if (lastAgentMessageIndex < 0) return null;
    return messages[lastAgentMessageIndex]?.id ?? null;
  }, [lastAgentMessageIndex, messages]);

  // Business rule: Use last user message as scroll target when a new response starts.
  const lastUserMessageId = useMemo(() => {
    const userMessages = messages.filter((msg) => msg.isUser);
    if (userMessages.length === 0) return null;
    return userMessages[userMessages.length - 1]?.id ?? null;
  }, [messages]);

  const handleTypingComplete = useCallback(() => {
    if (lastAgentMessageId) {
      setTypingCompleteMessageId(lastAgentMessageId);
    }
  }, [lastAgentMessageId]);

  const handleLearnMore = useCallback(
    (productName: string) => {
      if (onSendMessage) {
        void onSendMessage(`Tell me more about the Everlywell ${productName}`);
      }
    },
    [onSendMessage],
  );

  // Business rule: Recommendations appear only after typing completes or for restored messages.
  const isNewAgentMessage = Boolean(
    lastAgentMessageId &&
      !initialMessageIdsRef.current?.has(lastAgentMessageId),
  );
  const hasTypingCompleted = typingCompleteMessageId === lastAgentMessageId;
  const isRestoredMessage = Boolean(
    lastAgentMessageId &&
      initialMessageIdsRef.current?.has(lastAgentMessageId),
  );

  const isTypingComplete =
    !isStreaming &&
    !isThinking &&
    !isAwaitingAgentResponse &&
    (isRestoredMessage || hasTypingCompleted);

  // Notify parent when typing completion state changes (e.g., to enable input).
  useEffect(() => {
    if (onTypingComplete) {
      onTypingComplete(isTypingComplete);
    }
  }, [isTypingComplete, onTypingComplete]);

  // Business rule: Animate only new agent messages.
  const lastMessageShouldAnimate = isNewAgentMessage;

  // Guard: Recommendations must never show during thinking/streaming.
  const canShowRecommendations =
    !isThinking &&
    !isStreaming &&
    showLogoOnRecommendation &&
    Boolean(recommendations) &&
    isTypingComplete;

  // Business rule: Keep avatar on message while typing/streaming.
  const showLogoOnMessageDuringTyping =
    lastMessageShouldAnimate && !isTypingComplete && !isThinking;
  const adjustedShowLogoOnLastAgentMessage =
    showLogoOnLastAgentMessage ||
    showLogoOnMessageDuringTyping ||
    isStreaming;

  const lastAgentMessageRef = useRef<HTMLDivElement | null>(null);
  const lastUserMessageRef = useRef<HTMLDivElement | null>(null);

  const scrollDeps = useMemo(
    () => [...scrollDependencies, isAwaitingAgentResponse, lastAgentMessageId],
    [scrollDependencies, isAwaitingAgentResponse, lastAgentMessageId],
  );

  const scrollRef = useChatScrollAnchor({
    deps: scrollDeps,
    isAwaitingAgentResponse,
    anchorId: lastAgentMessageId,
    anchorRef: lastAgentMessageRef,
  });

  // Business rule: While typing, follow the stream so new characters stay visible.
  const isCurrentlyTyping = lastMessageShouldAnimate && !isTypingComplete;
  useEffect(() => {
    if (!isCurrentlyTyping) return;

    const scrollToBottom = () => {
      const scrollContainer = scrollRef.current;
      if (!scrollContainer) return;

      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth',
      });
    };

    let intervalId: ReturnType<typeof setInterval> | null = null;
    const delayTimeout = setTimeout(() => {
      scrollToBottom();
      intervalId = setInterval(scrollToBottom, 100);
    }, 600);

    return () => {
      clearTimeout(delayTimeout);
      if (intervalId) clearInterval(intervalId);
    };
  }, [isCurrentlyTyping, scrollRef]);

  // Business rule: Scroll to bottom once recommendations appear after typing completes.
  const prevCanShowRecommendationsRef = useRef(false);
  useEffect(() => {
    if (canShowRecommendations && !prevCanShowRecommendationsRef.current) {
      const scrollContainer = scrollRef.current;
      if (scrollContainer) {
        setTimeout(() => {
          scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: 'smooth',
          });
        }, 100);
      }
    }
    prevCanShowRecommendationsRef.current = Boolean(canShowRecommendations);
  }, [canShowRecommendations, scrollRef]);

  // Business rule: Scroll to last user message when a response starts.
  useEffect(() => {
    if (
      !lastUserMessageId ||
      !lastUserMessageRef.current ||
      !scrollRef.current
    ) {
      return;
    }
    if (!isAwaitingAgentResponse) return;

    const timeoutId = setTimeout(() => {
      const messageEl = lastUserMessageRef.current;
      const scrollContainer = scrollRef.current;
      if (!messageEl || !scrollContainer) return;

      // Scroll to message top minus a small offset for breathing room.
      const containerRect = scrollContainer.getBoundingClientRect();
      const messageRect = messageEl.getBoundingClientRect();
      const scrollTop = scrollContainer.scrollTop;
      const messageTop = messageRect.top - containerRect.top + scrollTop;

      scrollContainer.scrollTo({
        top: messageTop - 50,
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [lastUserMessageId, isAwaitingAgentResponse, scrollRef]);

  const getMessageAnimationState = useCallback(
    (message: Message, index: number): MessageAnimationState => {
      const isLastAgentMessage = index === lastAgentMessageIndex;
      const isActiveStreamingMessage =
        isStreaming && activeAgentMessageId === message.id;
      const shouldAnimate =
        !message.isUser &&
        isLastAgentMessage &&
        !isAwaitingAgentResponse &&
        !initialMessageIdsRef.current?.has(message.id) &&
        (isActiveStreamingMessage || !isStreaming);

      const shouldFadeOutAvatar =
        isLastAgentMessage &&
        shouldAnimate &&
        isTypingComplete &&
        Boolean(recommendations);

      return {
        shouldAnimate,
        shouldFadeOutAvatar,
        onTypingComplete: isLastAgentMessage ? handleTypingComplete : undefined,
      };
    },
    [
      lastAgentMessageIndex,
      isStreaming,
      activeAgentMessageId,
      isAwaitingAgentResponse,
      isTypingComplete,
      recommendations,
      handleTypingComplete,
    ],
  );

  return {
    lastAgentMessageIndex,
    showLogoOnRecommendation,
    showLogoOnError,
    adjustedShowLogoOnLastAgentMessage,
    isAwaitingAgentResponse,
    lastAgentMessageId,
    lastUserMessageId,
    lastAgentMessageRef,
    lastUserMessageRef,
    scrollRef,
    canShowRecommendations,
    lastMessageShouldAnimate,
    isTypingComplete,
    handleLearnMore,
    getMessageAnimationState,
  };
}
